'use client';

import {
  Alert,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useRouter } from 'next/navigation';

import { LimitedTextInput, TrackedButton } from '@/components';
import { TOPIC_MAX_LENGTH } from '@/constants';
import { useAuth, useIssuePage } from '@/contexts';
import { Entity_InfoFragment } from '@/graphql';

import styles from './IssueHeader.module.css';

interface IssueSupporterInfo {
  supporterCount?: number | null;
  topic?: string;
}

interface IssueHeaderProps {
  formData: {
    topic: string;
  };
  issue?: IssueSupporterInfo | null;
  displayLeader: Entity_InfoFragment;
  handleFormChange: (field: string, value: string) => void;
  verifiedVotersCount: number;
}

export const IssueHeader = ({
  formData,
  issue,
  displayLeader,
  handleFormChange,
  verifiedVotersCount,
}: IssueHeaderProps) => {
  const {
    pageState,
    isCreateModeWithNoInfo,
    isViewModeAsSupporter,
    getIsCreateMode,
  } = useIssuePage();

  const router = useRouter();
  const { isCurrentUserVerified } = useAuth();
  const [swayScoreModalOpened, { open, close }] = useDisclosure(false);
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  return (
    <>
      <Stack
        className={styles.voteHeader}
        gap="ml"
      >
        <Group className={styles.voteTitle}>
          <Text className={styles.voteWithText}>Vote with</Text>
          <Text className={styles.nameText}>
            {displayLeader?.displayNameShort || 'Your Name'}{' '}
            <span className={styles.onText}>on</span>
          </Text>
          <Group>
            {getIsCreateMode() ? (
              <Tooltip
                variant="primary-light"
                opened={
                  isCreateModeWithNoInfo(pageState) && !swayScoreModalOpened
                }
                label="Your topic on Sway is the specific issue or policy area you champion. Choose yours carefully; once set, it cannot be changed."
                position={isMobile ? 'bottom-start' : 'right'}
                offset={10}
                arrowOffset={10}
                arrowSize={15}
                withArrow
                multiline
              >
                <LimitedTextInput
                  variant="title"
                  value={formData.topic}
                  placeholder="Topic*"
                  onChange={(value) => handleFormChange('topic', value)}
                  maxLength={TOPIC_MAX_LENGTH}
                />
              </Tooltip>
            ) : (
              <span>
                {formData.topic.split(' ').map((word, index) => (
                  <span
                    key={index}
                    className={styles.topicText}
                  >
                    {word}&nbsp;
                  </span>
                ))}
              </span>
            )}
          </Group>
        </Group>
        <TrackedButton
          className={styles.swayScoreButton}
          onClick={open}
          variant="secondary"
          trackingName="issue_header_sway_score_click"
          trackingProperties={{
            leaderId: displayLeader?.id,
          }}
        >
          SWAY SCORE &nbsp; <strong> {verifiedVotersCount || '-'} </strong>
        </TrackedButton>
      </Stack>

      <Modal
        centered
        opened={swayScoreModalOpened}
        onClose={close}
        title="Sway Score"
      >
        <Stack gap="md">
          <Alert variant="dimmed">
            <Text>
              <strong>
                {verifiedVotersCount ?? 0} verified{' '}
                {verifiedVotersCount === 1 ? 'voter ' : 'voters '}{' '}
              </strong>
              {verifiedVotersCount === 1 ? ' supports' : ' support'}{' '}
              {displayLeader?.displayNameShort} on this issue.
            </Text>
          </Alert>
          <Text>
            A leader&apos;s Sway Score = verified voters following their ballot
            picks. The higher their score, the greater the political leverage!
          </Text>
          <Divider mb="sm" />

          <Stack gap="xs">
            <Text>Total supporter count: {issue?.supporterCount || 0} </Text>
            {isViewModeAsSupporter(pageState) && isCurrentUserVerified && (
              <Text
                c="var(--mantine-color-primary-6)"
                size="md"
              >
                You are one of {displayLeader?.displayNameShort}&apos;s verified
                supporters.
              </Text>
            )}
          </Stack>
          {isViewModeAsSupporter(pageState) && !isCurrentUserVerified && (
            <TrackedButton
              variant="link"
              onClick={() => router.push('/verify')}
              size="md"
              trackingName="issue_header_verify_vote_click"
              trackingProperties={{
                entityId: displayLeader?.id,
              }}
            >
              VERIFY YOUR VOTE
            </TrackedButton>
          )}
        </Stack>
      </Modal>
    </>
  );
};
